/**
 * ChunkErrorBoundary
 *
 * Catches chunk-load failures (404s after a deploy) and reloads the page.
 * Vite generates hashed chunk filenames — after a new deploy the old chunks
 * no longer exist on the server, causing dynamic imports to fail.
 */

import React from 'react';

interface State {
  hasError: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    error.name === 'ChunkLoadError'
  );
}

const RELOAD_KEY = 'chunk_error_reload';

export class ChunkErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State | null {
    if (isChunkLoadError(error)) return { hasError: true };
    return null; // re-throw non-chunk errors
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) throw error;

    // Prevent infinite reload loop: only auto-reload once per session
    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (!alreadyReloaded) {
      sessionStorage.setItem(RELOAD_KEY, '1');
      window.location.reload();
    }
  }

  handleReload = () => {
    sessionStorage.removeItem(RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>
            Se actualizó la aplicación. Recarga para continuar.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              padding: '8px 24px',
              fontSize: 14,
              cursor: 'pointer',
              borderRadius: 4,
              border: '1px solid #ccc',
              background: '#1976d2',
              color: '#fff',
            }}
          >
            Recargar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
